import { Link } from "react-router-dom";
import { ArrowRight, Leaf, ShieldCheck, Award, FileText, User } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { BrandRibbon } from "@/components/BrandRibbon";
import { useLang } from "@/context/LanguageContext";
import { BRAND } from "@/lib/site";

export default function About() {
  const { t } = useLang();
  const aboutTitle = t("about_title");
  const journey = t("journey");

  const credentials = [
    { icon: FileText, label: t("cred_gst"), value: BRAND.gst },
    { icon: Award, label: t("cred_udyam"), value: BRAND.udyam },
    { icon: ShieldCheck, label: t("cred_fssai"), value: BRAND.fssai },
    { icon: User, label: t("cred_director"), value: BRAND.director },
  ];

  return (
    <div className="pt-36 pb-24" data-testid="about-page">
      <section className="mx-auto max-w-7xl px-5 md:px-10">
        <p className="label-eyebrow">{t("about_eyebrow")}</p>
        <h1 className="mt-5 font-serif font-light text-[clamp(2.6rem,7vw,6rem)] leading-[0.98] text-white">
          <MaskedLines lines={aboutTitle.map((l, i) => (i === 1 ? <span key={i} className="text-gold-gradient italic">{l}</span> : l))} />
        </h1>
        <Reveal delay={0.5} className="mt-8 grid lg:grid-cols-2 gap-10 items-start">
          <p className="text-xl text-white/70 font-light leading-relaxed">{t("about_p1")}</p>
          <p className="text-white/60 font-light leading-relaxed">{t("about_p2")}</p>
        </Reveal>
      </section>

      <div className="my-20">
        <BrandRibbon items={t("ribbon_about")} />
      </div>

      <section className="mx-auto max-w-7xl px-5 md:px-10">
        <Reveal className="mb-14 max-w-xl">
          <p className="label-eyebrow">{t("journey_eyebrow")}</p>
          <h2 className="mt-3 font-serif font-light text-4xl md:text-6xl text-white leading-tight">
            {t("journey_t1")} <span className="text-gold-gradient italic">{t("journey_t2")}</span>
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-10 md:gap-6">
          {journey.map((j, i) => (
            <Reveal key={i} delay={i * 0.12} className="relative pt-8 border-t border-gold/20">
              <span className="label-eyebrow">{j.year}</span>
              <h3 className="mt-3 font-serif text-3xl text-white">{j.title}</h3>
              <p className="mt-3 text-white/60 font-light leading-relaxed">{j.body}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 md:px-10 mt-24">
        <Reveal className="relative overflow-hidden rounded-3xl border border-gold/20 bg-navy-800 p-10 md:p-16">
          <div className="absolute -top-20 -left-20 h-56 w-56 radial-glow" />
          <div className="relative grid lg:grid-cols-[auto_1fr] gap-10 items-center">
            <img src="/assets/logo.png" alt="Mulywan emblem" className="h-28 w-28 rounded-full ring-1 ring-gold/40 mx-auto" />
            <div>
              <span className="font-serif text-6xl text-gold/25 leading-none">"</span>
              <p className="font-serif text-2xl md:text-3xl text-white/90 italic leading-relaxed -mt-6">{t("director_quote")}</p>
              <p className="mt-6 text-sm uppercase tracking-[0.2em] text-gold">{t("director_attr")}</p>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 md:px-10 mt-24">
        <Reveal className="mb-10">
          <p className="label-eyebrow flex items-center gap-3"><Leaf size={16} className="text-gold" /> {t("cred_eyebrow")}</p>
          <h2 className="mt-3 font-serif font-light text-4xl md:text-5xl text-white">{t("cred_title")}</h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {credentials.map((c, i) => (
            <Reveal key={c.label} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-gold/15 bg-navy-800 p-7 hover:border-gold/40 transition-colors">
                <c.icon className="text-gold" size={26} />
                <p className="mt-5 text-xs uppercase tracking-[0.18em] text-white/45">{c.label}</p>
                <p className="mt-1.5 font-serif text-2xl text-white break-words">{c.value}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2} className="mt-6 rounded-2xl border border-gold/15 bg-navy-900 p-7">
          <p className="text-xs uppercase tracking-[0.18em] text-white/45">{t("reg_office")}</p>
          <p className="mt-2 font-serif text-2xl md:text-3xl text-white">{BRAND.address}</p>
          <p className="mt-2 text-white/55 font-light">{t("registered_on")} {BRAND.registered} • {t("proprietorship")}</p>
        </Reveal>

        <Reveal delay={0.25} className="mt-12 text-center">
          <Link to="/products" className="group inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm uppercase tracking-[0.2em] font-semibold text-navy-900 hover:bg-gold-light transition-colors">
            {t("explore_products")} <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
